import { dismissNativeSplashForUpdateReload } from './lib/updateReloadSplash'

void dismissNativeSplashForUpdateReload()

import { initSentry } from './lib/sentry'
import { Capacitor } from '@capacitor/core'
import { bootstrapNativeStatusBarAppearance } from './lib/nativeStatusBar'

// Initialize Sentry before anything else so it captures all errors from the start
initSentry()

if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
  document.documentElement.classList.add('cap-ios-native')
}

if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
  document.documentElement.classList.add('cap-android-native')
}

bootstrapNativeStatusBarAppearance()

import { createRoot } from 'react-dom/client'
import { PostHogProvider } from 'posthog-js/react'
import { posthog } from './lib/posthog'
import { initializeViewportHeight } from './utils/viewport'
import { applyTrackingConsent, getStoredAnalyticsConsent } from './lib/trackingConsent'
import 'flag-icons/css/flag-icons.min.css'
import './index.css'
import App from './App.tsx'
import { auth } from './lib/firebase'

// Initialize viewport height handling for mobile
initializeViewportHeight();

// Apply persisted consent on startup so analytics trackers are loaded only when allowed.
applyTrackingConsent(getStoredAnalyticsConsent());

// Disable console logs in production while preserving error logging
// Exception: Allow full console logs for keijo@getmoretats.com account
if (import.meta.env.PROD) {
  
  // Check if current user is keijo@getmoretats.com or test@artist.com or test@getmoretats.com
  let isDebugUser = false
  auth.onAuthStateChanged((user) => {
    if (user && (user.email === 'keijo@getmoretats.com' || user.email === 'test@artist.com' || user.email === 'test@getmoretats.com' || user.email === 'keijokristian.kunnari@gmail.com')) {
      isDebugUser = true
      console.log('🔧 Debug mode enabled for keijo@getmoretats.com')
    }
  })

  // Store original error and warn methods
  const originalError = console.error
  const originalWarn = console.warn
  
  // List of methods to disable
  const methodsToDisable = [
    'log',
    'info',
    'debug',
    'trace',
    'table',
    'group',
    'groupCollapsed',
    'groupEnd',
    'count',
    'countReset',
    'time',
    'timeLog',
    'timeEnd'
  ]

  // Disable non-critical console methods (unless debug user)
  methodsToDisable.forEach(method => {
    // TypeScript type assertion to handle dynamic property access
    const originalMethod = (console as any)[method]
    ;(console as any)[method] = (...args: any[]) => {
      if (isDebugUser) {
        originalMethod.apply(console, args)
      }
    }
  })

  // Keep error but strip any sensitive data/stack traces (unless debug user)
  console.error = (...args) => {
    if (isDebugUser) {
      originalError.apply(console, args)
    } else {
      const sanitizedArgs = args.map(arg => 
        arg instanceof Error 
          ? `Error: ${arg.message}` // Strip stack trace
          : typeof arg === 'object' 
            ? '[Object]' // Hide object contents
            : arg
      )
      originalError.apply(console, sanitizedArgs)
    }
  }

  // Keep warnings but strip sensitive data (unless debug user)
  console.warn = (...args) => {
    if (isDebugUser) {
      originalWarn.apply(console, args)
    } else {
      const sanitizedArgs = args.map(arg => 
        typeof arg === 'object' ? '[Object]' : arg
      )
      originalWarn.apply(console, sanitizedArgs)
    }
  }
}

const rootElement = document.getElementById('root')!
try {
  createRoot(rootElement).render(
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  )
} catch (err) {
  // Fallback: show a helpful error message on the live site so we can debug
  console.error('Fatal render error', err)
  if (rootElement) {
    rootElement.innerHTML = `<pre style="white-space:pre-wrap;color:red;padding:1rem;font-family:monospace;">${(err as Error)?.stack ?? err}</pre>`
  }
}
